import { useAuth0, User } from '@auth0/auth0-react'

// Dev mode check (only in dev builds — import.meta.env.DEV is always false in production)
const isDevAuthDisabled = import.meta.env.DEV && import.meta.env.VITE_DISABLE_AUTH === 'true'

// Dev role override (only used when VITE_DISABLE_AUTH=true)
// Set VITE_DEV_ROLE=user to test as regular user, or VITE_DEV_ROLE=admin for admin
const devRole = import.meta.env.VITE_DEV_ROLE || 'admin'

// Auth0 namespace for custom claims
const ROLES_NAMESPACE = 'https://lextool.pl/roles'

// Mock user for dev mode
const devUser: User = {
  name: 'Dev User',
  email: 'dev@lextool.pl',
  sub: 'dev|123456',
  picture: 'https://ui-avatars.com/api/?name=Dev+User&background=0D8ABC&color=fff',
  [ROLES_NAMESPACE]: devRole === 'admin' ? ['admin'] : devRole === 'lawyer' ? ['lawyer'] : [],
}

export interface UserRoleResult {
  isAdmin: boolean
  isLawyer: boolean
  user: User | undefined
  isLoading: boolean
  isAuthenticated: boolean
}

/**
 * Hook to check user roles from Auth0 token or dev mode
 *
 * Roles are expected in the Auth0 token under the namespace:
 * "https://lextool.pl/roles": ["admin", "lawyer"]
 *
 * In dev mode (VITE_DISABLE_AUTH=true), use VITE_DEV_ROLE to set role:
 * - VITE_DEV_ROLE=admin  → admin user (default)
 * - VITE_DEV_ROLE=user   → regular user
 * - VITE_DEV_ROLE=lawyer → lawyer user
 */
export function useUserRole(): UserRoleResult {
  // Always call useAuth0 but handle the case when provider is missing
  const auth0 = useAuth0()

  // When auth is disabled, return mock authenticated user
  if (isDevAuthDisabled) {
    const isAdmin = devRole === 'admin'
    const isLawyer = devRole === 'admin' || devRole === 'lawyer'

    return {
      isAdmin,
      isLawyer,
      user: devUser,
      isLoading: false,
      isAuthenticated: true
    }
  }

  const { user, isLoading, isAuthenticated } = auth0

  // Get roles from Auth0 token (namespace claim) or legacy role field
  const roles: string[] = user?.[ROLES_NAMESPACE] || []
  const legacyRole = user?.role as string | undefined

  const isAdmin = roles.includes('admin') || legacyRole === 'admin'
  const isLawyer = isAdmin || roles.includes('lawyer')

  return {
    isAdmin,
    isLawyer,
    user,
    isLoading,
    isAuthenticated
  }
}
